import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useApiMutation } from '@/hooks/useApi';
import { useProfile } from '@/hooks/useProfile';
import { hasStaffProfile } from '@/util/staffUtils';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface FeatureFlagsContextType {
  featureFlags: string[];
  featureFlagsStatus: 'IDLE' | 'LOADING' | 'COMPLETED';
}

export const FeatureFlagsContext = createContext<
  FeatureFlagsContextType | undefined
>(undefined);

const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const featureFlagsFetchInitiated = useRef(false);
  const { profile } = useProfile();

  // An untrusted cached profile counts as "not impersonating yet", so this stays
  // consistent with what ProfileProvider actually hydrates. Computed once: it's
  // only read by the mount effect below, which latches featureFlagsFetchInitiated
  // permanently on its first run, so re-checking on every later render can
  // never change the outcome — just costs a repeated localStorage read/parse.
  const isStaff = useMemo(
    () => process.env.REACT_APP_ENV === 'staff' && !hasStaffProfile(),
    [],
  );

  const [featureFlags, setFeatureFlags] = useState<string[]>([]);
  const [featureFlagsStatus, setFeatureFlagsStatus] = useState<
    'IDLE' | 'LOADING' | 'COMPLETED'
  >('IDLE');

  const featureToggleMutation = useApiMutation<
    { expires: string; keys: string[] },
    { userId: string }
  >({
    url: '/feature-toggles',
    method: 'post',
    endpoint: 'prod',
  });

  const getFeatureFlags = useCallback(async () => {
    if (!profile || !profile.value[0].MemberEmail) return;
    try {
      setFeatureFlagsStatus('LOADING');
      const featureFlags = await featureToggleMutation.mutateAsync({
        userId: profile.value[0].eXpMember.Uuid,
      });
      setFeatureFlags(featureFlags.keys);
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    } finally {
      setFeatureFlagsStatus('COMPLETED');
    }
  }, [profile, featureToggleMutation]);

  useEffect(() => {
    if (
      isStaff &&
      featureFlagsStatus === 'IDLE' &&
      !featureFlagsFetchInitiated.current
    ) {
      featureFlagsFetchInitiated.current = true;
      setFeatureFlags([FEATURE_FLAGS.ENABLE_AGENT_PLATFORM]);
      setFeatureFlagsStatus('COMPLETED');
      return;
    }

    // Only fetch feature flags if we have a profile, and we haven't initiated a fetch yet
    if (
      profile &&
      profile.value[0].eXpMember.Uuid &&
      featureFlagsStatus === 'IDLE' &&
      !featureFlagsFetchInitiated.current
    ) {
      getFeatureFlags();
    }
  }, [profile, featureFlagsStatus, getFeatureFlags, isStaff]);

  return (
    <FeatureFlagsContext.Provider value={{ featureFlags, featureFlagsStatus }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export default FeatureFlagsProvider;
